const API_BASE = '/api';

/** @type {string|null} */
let authToken = localStorage.getItem('token');
/** @type {string|null} */
let activeCampaignId = localStorage.getItem('campaignId');

export function setToken(token) {
  authToken = token;
  if (token) {
    localStorage.setItem('token', token);
  } else {
    localStorage.removeItem('token');
  }
}

export function getToken() {
  return authToken;
}

export function setCampaignId(id) {
  activeCampaignId = id;
  if (id) {
    localStorage.setItem('campaignId', id);
  } else {
    localStorage.removeItem('campaignId');
  }
}

export function getCampaignId() {
  return activeCampaignId;
}

/** @type {Promise|null} - Prevents multiple concurrent refresh attempts */
let refreshPromise = null;

/**
 * Try to refresh the JWT token using the refresh endpoint.
 * @returns {Promise<boolean>} true if refresh succeeded
 */
async function tryRefreshToken() {
  if (!authToken) return false;
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });
      if (!res.ok) return false;
      const data = await res.json();
      setToken(data.token);
      return true;
    } catch (_err) {
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/**
 * Generic API fetch wrapper with automatic token refresh on 401.
 * @param {string} path
 * @param {RequestInit} [options]
 */
export async function apiFetch(path, options = {}) {
  const headers = { ...options.headers };

  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }
  if (activeCampaignId) {
    headers['X-Campaign-Id'] = activeCampaignId;
  }
  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });

  if (response.status === 401 && authToken) {
    // Try refresh before giving up
    const refreshed = await tryRefreshToken();
    if (refreshed) {
      // Retry the original request with the new token
      headers['Authorization'] = `Bearer ${authToken}`;
      const retry = await fetch(`${API_BASE}${path}`, { ...options, headers });
      if (retry.ok) {
        return retry.json();
      }
    }
    setToken(null);
    window.location.href = '/login';
    throw new Error('Session expiree');
  }

  if (response.status === 409) {
    throw Object.assign(
      new Error('Conflit : cette unite a ete modifiee par un autre utilisateur. Rechargez.'),
      { status: 409 }
    );
  }

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || `Erreur ${response.status}`);
  }

  return data;
}

// Auth
export const auth = {
  login: (email, password) => apiFetch('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  pinLogin: (email, pin) => apiFetch('/auth/pin-login', { method: 'POST', body: JSON.stringify({ email, pin }) }),
  setPin: (pin) => apiFetch('/auth/set-pin', { method: 'POST', body: JSON.stringify({ pin }) }),
  me: () => apiFetch('/auth/me'),
  register: (data) => apiFetch('/auth/register', { method: 'POST', body: JSON.stringify(data) }),
  revokeSessions: (userId) => apiFetch(`/auth/revoke-sessions/${userId}`, { method: 'POST', body: JSON.stringify({}) }),
  generateInvite: (campaignId, role) => apiFetch('/auth/invite', { method: 'POST', body: JSON.stringify({ campaignId, role }) }),
};

// Admin global
export const admin = {
  listUsers: () => apiFetch('/admin/users'),
  impersonate: (userId, reason) => apiFetch(`/admin/impersonate/${userId}`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  }),
  stopImpersonation: () => apiFetch('/admin/impersonate-stop', {
    method: 'POST',
    body: JSON.stringify({}),
  }),
};

// Campaigns
export const campaigns = {
  list: () => apiFetch('/campaigns'),
  get: (id) => apiFetch(`/campaigns/${id}`),
  create: (data) => apiFetch('/campaigns', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiFetch(`/campaigns/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  addUser: (id, userId, role) => apiFetch(`/campaigns/${id}/users`, { method: 'POST', body: JSON.stringify({ userId, role }) }),
  listUsers: (id) => apiFetch(`/campaigns/${id}/users`),
  updateUserRole: (id, userId, role) => apiFetch(`/campaigns/${id}/users/${userId}`, { method: 'PUT', body: JSON.stringify({ role }) }),
  removeUser: (id, userId) => apiFetch(`/campaigns/${id}/users/${userId}`, { method: 'DELETE' }),
};

// Clients (scoped by campaign via X-Campaign-Id header)
export const clients = {
  list: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiFetch(`/c/${activeCampaignId}/clients${qs ? '?' + qs : ''}`);
  },
  get: (id) => apiFetch(`/c/${activeCampaignId}/clients/${id}`),
  getItems: (id) => apiFetch(`/c/${activeCampaignId}/clients/${id}/items`),
  getHistory: (id) => apiFetch(`/c/${activeCampaignId}/clients/${id}/history`),
  getDeliverySummary: (id) => apiFetch(`/c/${activeCampaignId}/clients/${id}/delivery-summary`),
  getCities: () => apiFetch(`/c/${activeCampaignId}/clients/cities`),
  update: (id, data) => apiFetch(`/c/${activeCampaignId}/clients/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  import: (file) => {
    const form = new FormData();
    form.append('file', file);
    return apiFetch(`/c/${activeCampaignId}/clients/import`, { method: 'POST', body: form });
  },
  importItems: (file) => {
    const form = new FormData();
    form.append('file', file);
    return apiFetch(`/c/${activeCampaignId}/clients/items/import`, { method: 'POST', body: form });
  },
};

// Plans (scoped by campaign)
export const plans = {
  list: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiFetch(`/c/${activeCampaignId}/plans${qs ? '?' + qs : ''}`);
  },
  get: (id) => apiFetch(`/c/${activeCampaignId}/plans/${id}`),
  create: (data) => apiFetch(`/c/${activeCampaignId}/plans`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  createMultiDay: (data) => apiFetch(`/c/${activeCampaignId}/plans/multi`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  reorder: (planId, stops) => apiFetch(`/c/${activeCampaignId}/plans/${planId}/reorder`, {
    method: 'PUT',
    body: JSON.stringify({ stops }),
  }),
  updateStop: (planId, stopId, data) => apiFetch(`/c/${activeCampaignId}/plans/${planId}/stops/${stopId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id) => apiFetch(`/c/${activeCampaignId}/plans/${id}`, { method: 'DELETE' }),
  generateTracking: (id) => apiFetch(`/c/${activeCampaignId}/plans/${id}/tracking`, { method: 'POST', body: JSON.stringify({}) }),
  duplicate: (id, date) => apiFetch(`/c/${activeCampaignId}/plans/${id}/duplicate`, {
    method: 'POST',
    body: JSON.stringify({ date }),
  }),
  moveStop: (stopId, targetPlanId) => apiFetch(`/c/${activeCampaignId}/plans/stops/${stopId}/move`, {
    method: 'PUT',
    body: JSON.stringify({ targetPlanId }),
  }),
};

// Re-export campaign-scoped modules from campaign.js
export { stats, sms, messages, delivery, ds3, downloadExport } from './campaign.js';
